/**
 * 任务标签管理系统
 * 
 * 提供标签的增删改查、统计分析、智能建议等功能
 */

import { Task, TaskPriority } from '../types';
import { logger } from '../utils/logger';
import path from 'path';
import fs from 'fs-extra';

export interface TagInfo {
  name: string;
  count: number;
  lastUsed: Date;
  color?: string;
  description?: string;
  category?: string;
}

export interface TagStats {
  totalTags: number;
  totalTaggedTasks: number;
  topTags: Array<{ tag: string; count: number }>;
  recentTags: string[];
  tagsByCategory: Record<string, string[]>;
}

export interface TagSuggestion {
  tag: string;
  reason: string;
  confidence: number;
}

export interface TagGroup {
  id: string;
  name: string;
  description?: string;
  tags: string[];
  color?: string;
  createdAt: Date;
  updatedAt: Date;
}

export class TagManager {
  private dataDir: string;
  private tagIndexFile: string;
  private tagGroupsFile: string;
  private tagIndex: Map<string, TagInfo> = new Map();
  private tagGroups: Map<string, TagGroup> = new Map();
  
  // 预定义的系统标签
  private systemTags = {
    priority: ['urgent', 'important', 'low-priority'],
    complexity: ['simple', 'medium', 'complex', 'needs-split'],
    type: ['feature', 'bug', 'refactor', 'docs', 'test', 'chore'],
    status: ['blocked', 'in-review', 'ready', 'wip'],
    team: ['frontend', 'backend', 'devops', 'design', 'qa'],
    phase: ['planning', 'development', 'testing', 'deployment']
  };
  
  // 标签颜色映射
  private tagColors = {
    urgent: '#ff4444',
    important: '#ff8800',
    bug: '#d73a49',
    feature: '#0366d6',
    test: '#28a745',
    docs: '#6f42c1',
    blocked: '#e36209',
    wip: '#ffd33d',
    frontend: '#61dafb',
    backend: '#339933',
    complex: '#b60205',
    simple: '#22863a'
  };
  
  constructor(workflowDir: string) {
    this.dataDir = path.join(workflowDir, 'tags');
    this.tagIndexFile = path.join(this.dataDir, 'tag-index.json');
    this.tagGroupsFile = path.join(this.dataDir, 'tag-groups.json');
  }
  
  /**
   * 初始化标签管理器
   */
  async initialize(): Promise<void> {
    await fs.ensureDir(this.dataDir);
    await this.loadTagIndex();
    await this.loadTagGroups();
    
    // 初始化系统标签
    for (const [category, tags] of Object.entries(this.systemTags)) {
      for (const tag of tags) {
        if (!this.tagIndex.has(tag)) {
          this.tagIndex.set(tag, {
            name: tag,
            count: 0,
            lastUsed: new Date(),
            color: this.tagColors[tag],
            category
          });
        }
      }
    }
    
    await this.saveTagIndex();
  }
  
  /**
   * 加载标签索引
   */
  private async loadTagIndex(): Promise<void> {
    try {
      if (await fs.pathExists(this.tagIndexFile)) {
        const data = await fs.readJson(this.tagIndexFile);
        this.tagIndex = new Map(
          data.tags.map((tag: TagInfo) => [tag.name, {
            ...tag,
            lastUsed: new Date(tag.lastUsed)
          }])
        );
      }
    } catch (error) {
      logger.error('Failed to load tag index:', error);
    }
  }
  
  /**
   * 保存标签索引
   */
  private async saveTagIndex(): Promise<void> {
    try {
      const data = {
        version: '1.0.0',
        updatedAt: new Date(),
        tags: Array.from(this.tagIndex.values())
      };
      await fs.writeJson(this.tagIndexFile, data, { spaces: 2 });
    } catch (error) {
      logger.error('Failed to save tag index:', error);
    }
  }
  
  /**
   * 加载标签组
   */
  private async loadTagGroups(): Promise<void> {
    try {
      if (await fs.pathExists(this.tagGroupsFile)) {
        const data = await fs.readJson(this.tagGroupsFile);
        this.tagGroups = new Map(
          data.groups.map((group: TagGroup) => [group.id, {
            ...group,
            createdAt: new Date(group.createdAt),
            updatedAt: new Date(group.updatedAt)
          }])
        );
      }
    } catch (error) {
      logger.error('Failed to load tag groups:', error);
    }
  }
  
  /**
   * 保存标签组
   */
  private async saveTagGroups(): Promise<void> {
    try {
      const data = {
        version: '1.0.0',
        updatedAt: new Date(),
        groups: Array.from(this.tagGroups.values())
      };
      await fs.writeJson(this.tagGroupsFile, data, { spaces: 2 });
    } catch (error) {
      logger.error('Failed to save tag groups:', error);
    }
  }
  
  /**
   * 为任务添加标签
   */
  async addTags(task: Task, tags: string[]): Promise<Task> {
    const normalizedTags = tags.map(tag => this.normalizeTag(tag));
    const currentTags = new Set(task.tags || []);
    
    for (const tag of normalizedTags) {
      currentTags.add(tag);
      await this.incrementTagUsage(tag);
    }
    
    task.tags = Array.from(currentTags);
    task.updatedAt = new Date();
    
    return task;
  }
  
  /**
   * 从任务移除标签
   */
  async removeTags(task: Task, tags: string[]): Promise<Task> {
    if (!task.tags) return task;
    
    const normalizedTags = tags.map(tag => this.normalizeTag(tag));
    const currentTags = new Set(task.tags);
    
    for (const tag of normalizedTags) {
      currentTags.delete(tag);
      await this.decrementTagUsage(tag);
    }
    
    task.tags = Array.from(currentTags);
    task.updatedAt = new Date();
    
    return task;
  }
  
  /**
   * 清空任务的所有标签
   */
  async clearTags(task: Task): Promise<Task> {
    if (task.tags) {
      for (const tag of task.tags) {
        await this.decrementTagUsage(tag);
      }
    }
    
    task.tags = [];
    task.updatedAt = new Date();
    
    return task;
  }
  
  /**
   * 按标签过滤任务
   */
  filterByTags(tasks: Task[], tags: string[], mode: 'any' | 'all' = 'any'): Task[] {
    const normalizedTags = tags.map(tag => this.normalizeTag(tag));
    
    return tasks.filter(task => {
      if (!task.tags || task.tags.length === 0) return false;
      
      if (mode === 'any') {
        return normalizedTags.some(tag => task.tags!.includes(tag));
      } else {
        return normalizedTags.every(tag => task.tags!.includes(tag));
      }
    });
  }
  
  /**
   * 按标签分组任务
   */
  groupByTags(tasks: Task[]): Map<string, Task[]> {
    const groups = new Map<string, Task[]>();
    
    // 添加未标记组
    groups.set('untagged', tasks.filter(task => !task.tags || task.tags.length === 0));
    
    // 按每个标签分组
    for (const task of tasks) {
      if (task.tags) {
        for (const tag of task.tags) {
          if (!groups.has(tag)) {
            groups.set(tag, []);
          }
          groups.get(tag)!.push(task);
        }
      }
    }
    
    return groups;
  }
  
  /**
   * 获取标签统计信息
   */
  async getStats(tasks: Task[]): Promise<TagStats> {
    const tagCounts = new Map<string, number>();
    let taggedTaskCount = 0;
    
    for (const task of tasks) {
      if (task.tags && task.tags.length > 0) {
        taggedTaskCount++;
        for (const tag of task.tags) {
          tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
        }
      }
    }
    
    // 获取 top 标签
    const topTags = Array.from(tagCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([tag, count]) => ({ tag, count }));
    
    // 获取最近使用的标签
    const recentTags = Array.from(this.tagIndex.values())
      .sort((a, b) => b.lastUsed.getTime() - a.lastUsed.getTime())
      .slice(0, 10)
      .map(tag => tag.name);
    
    // 按类别分组
    const tagsByCategory: Record<string, string[]> = {};
    for (const tagInfo of this.tagIndex.values()) {
      if (tagInfo.category) {
        if (!tagsByCategory[tagInfo.category]) {
          tagsByCategory[tagInfo.category] = [];
        }
        tagsByCategory[tagInfo.category].push(tagInfo.name);
      }
    }
    
    return {
      totalTags: this.tagIndex.size,
      totalTaggedTasks: taggedTaskCount,
      topTags,
      recentTags,
      tagsByCategory
    };
  }
  
  /**
   * 为任务建议标签
   */
  async suggestTags(task: Task, existingTasks?: Task[]): Promise<TagSuggestion[]> {
    const suggestions: TagSuggestion[] = [];
    const content = task.content.toLowerCase();
    const currentTags = new Set(task.tags || []);
    
    // 基于任务内容的关键词
    const keywordMappings = {
      'bug': ['bug', 'fix', 'error', 'issue', 'problem', '修复', '错误'],
      'feature': ['feature', 'implement', 'add', 'new', '功能', '实现', '添加'],
      'test': ['test', 'testing', 'spec', 'unit', 'e2e', '测试'],
      'docs': ['document', 'docs', 'readme', 'guide', '文档'],
      'refactor': ['refactor', 'cleanup', 'improve', '重构', '优化'],
      'urgent': ['urgent', 'asap', 'critical', 'emergency', '紧急', '立即'],
      'blocked': ['blocked', 'waiting', 'depend', '阻塞', '等待']
    };
    
    for (const [tag, keywords] of Object.entries(keywordMappings)) {
      if (!currentTags.has(tag) && keywords.some(keyword => content.includes(keyword))) {
        suggestions.push({
          tag,
          reason: `任务内容包含相关关键词`,
          confidence: 0.8
        });
      }
    }
    
    // 基于优先级
    if (task.priority === 'high' && !currentTags.has('important')) {
      suggestions.push({
        tag: 'important',
        reason: '高优先级任务',
        confidence: 0.9
      });
    }
    
    // 基于复杂度（如果有）
    if (task.content.length > 200 && !currentTags.has('complex')) {
      suggestions.push({
        tag: 'complex',
        reason: '任务描述较长，可能比较复杂',
        confidence: 0.6
      });
    }
    
    // 基于相似任务的标签
    if (existingTasks) {
      const similarTasks = this.findSimilarTasks(task, existingTasks);
      const tagFrequency = new Map<string, number>();
      
      for (const similarTask of similarTasks) {
        if (similarTask.tags) {
          for (const tag of similarTask.tags) {
            if (!currentTags.has(tag)) {
              tagFrequency.set(tag, (tagFrequency.get(tag) || 0) + 1);
            }
          }
        }
      }
      
      // 添加高频标签建议
      for (const [tag, freq] of tagFrequency.entries()) {
        if (freq >= 2) {
          suggestions.push({
            tag,
            reason: `${freq} 个相似任务使用了此标签`,
            confidence: Math.min(0.9, 0.5 + freq * 0.1)
          });
        }
      }
    }
    
    // 按置信度排序
    return suggestions.sort((a, b) => b.confidence - a.confidence);
  }
  
  /**
   * 创建标签组
   */
  async createGroup(name: string, description?: string, tags: string[] = []): Promise<TagGroup> {
    const id = `group-${Date.now()}`;
    const group: TagGroup = {
      id,
      name,
      description,
      tags: tags.map(tag => this.normalizeTag(tag)),
      createdAt: new Date(),
      updatedAt: new Date()
    };
    
    this.tagGroups.set(id, group);
    await this.saveTagGroups();
    
    return group;
  }
  
  /**
   * 更新标签组
   */
  async updateGroup(id: string, updates: Partial<TagGroup>): Promise<TagGroup | null> {
    const group = this.tagGroups.get(id);
    if (!group) return null;
    
    Object.assign(group, updates, { updatedAt: new Date() });
    
    if (updates.tags) {
      group.tags = updates.tags.map(tag => this.normalizeTag(tag));
    }
    
    await this.saveTagGroups();
    return group;
  }
  
  /**
   * 删除标签组
   */
  async deleteGroup(id: string): Promise<boolean> {
    const deleted = this.tagGroups.delete(id);
    if (deleted) {
      await this.saveTagGroups();
    }
    return deleted;
  }
  
  /**
   * 获取所有标签组
   */
  getGroups(): TagGroup[] {
    return Array.from(this.tagGroups.values());
  }
  
  /**
   * 获取所有标签
   */
  getAllTags(): TagInfo[] {
    return Array.from(this.tagIndex.values());
  }
  
  /**
   * 搜索标签
   */
  searchTags(query: string): TagInfo[] {
    const normalizedQuery = query.toLowerCase();
    return Array.from(this.tagIndex.values()).filter(tag =>
      tag.name.toLowerCase().includes(normalizedQuery) ||
      (tag.description && tag.description.toLowerCase().includes(normalizedQuery))
    );
  }
  
  /**
   * 重命名标签
   */
  async renameTag(oldName: string, newName: string, tasks: Task[]): Promise<number> {
    const normalizedOld = this.normalizeTag(oldName);
    const normalizedNew = this.normalizeTag(newName);
    
    if (normalizedOld === normalizedNew) return 0;
    
    let updatedCount = 0;
    
    // 更新所有任务中的标签
    for (const task of tasks) {
      if (task.tags && task.tags.includes(normalizedOld)) {
        const index = task.tags.indexOf(normalizedOld);
        task.tags[index] = normalizedNew;
        task.updatedAt = new Date();
        updatedCount++;
      }
    }
    
    // 更新标签索引
    const oldTagInfo = this.tagIndex.get(normalizedOld);
    if (oldTagInfo) {
      this.tagIndex.delete(normalizedOld);
      oldTagInfo.name = normalizedNew;
      this.tagIndex.set(normalizedNew, oldTagInfo);
    }
    
    // 更新标签组
    for (const group of this.tagGroups.values()) {
      const index = group.tags.indexOf(normalizedOld);
      if (index !== -1) {
        group.tags[index] = normalizedNew;
        group.updatedAt = new Date();
      }
    }
    
    await this.saveTagIndex();
    await this.saveTagGroups();
    
    return updatedCount;
  }
  
  /**
   * 合并标签
   */
  async mergeTags(sourceTags: string[], targetTag: string, tasks: Task[]): Promise<number> {
    const normalizedSources = sourceTags.map(tag => this.normalizeTag(tag));
    const normalizedTarget = this.normalizeTag(targetTag);
    
    let updatedCount = 0;
    
    for (const task of tasks) {
      if (!task.tags) continue;
      
      let updated = false;
      const newTags = new Set(task.tags);
      
      for (const sourceTag of normalizedSources) {
        if (newTags.has(sourceTag)) {
          newTags.delete(sourceTag);
          newTags.add(normalizedTarget);
          updated = true;
        }
      }
      
      if (updated) {
        task.tags = Array.from(newTags);
        task.updatedAt = new Date();
        updatedCount++;
      }
    }
    
    // 更新标签计数
    let totalCount = 0;
    for (const sourceTag of normalizedSources) {
      const tagInfo = this.tagIndex.get(sourceTag);
      if (tagInfo) {
        totalCount += tagInfo.count;
        this.tagIndex.delete(sourceTag);
      }
    }
    
    await this.incrementTagUsage(normalizedTarget, totalCount);
    
    await this.saveTagIndex();
    return updatedCount;
  }
  
  // 私有辅助方法
  
  /**
   * 标准化标签名称
   */
  private normalizeTag(tag: string): string {
    return tag.toLowerCase().trim().replace(/\s+/g, '-');
  }
  
  /**
   * 增加标签使用计数
   */
  private async incrementTagUsage(tag: string, count: number = 1): Promise<void> {
    const tagInfo = this.tagIndex.get(tag) || {
      name: tag,
      count: 0,
      lastUsed: new Date()
    };
    
    tagInfo.count += count;
    tagInfo.lastUsed = new Date();
    
    this.tagIndex.set(tag, tagInfo);
    await this.saveTagIndex();
  }
  
  /**
   * 减少标签使用计数
   */
  private async decrementTagUsage(tag: string): Promise<void> {
    const tagInfo = this.tagIndex.get(tag);
    if (tagInfo && tagInfo.count > 0) {
      tagInfo.count--;
      
      if (tagInfo.count === 0 && !this.isSystemTag(tag)) {
        // 如果不是系统标签且计数为0，可以考虑删除
        // 但这里我们保留它，以保存历史记录
      }
      
      await this.saveTagIndex();
    }
  }
  
  /**
   * 检查是否为系统标签
   */
  private isSystemTag(tag: string): boolean {
    return Object.values(this.systemTags).flat().includes(tag);
  }
  
  /**
   * 查找相似任务
   */
  private findSimilarTasks(task: Task, tasks: Task[]): Task[] {
    const keywords = this.extractKeywords(task.content);
    const similar: Array<{ task: Task; score: number }> = [];
    
    for (const otherTask of tasks) {
      if (otherTask.id === task.id) continue;
      
      const otherKeywords = this.extractKeywords(otherTask.content);
      const commonKeywords = keywords.filter(k => otherKeywords.includes(k));
      const score = commonKeywords.length / Math.max(keywords.length, otherKeywords.length);
      
      if (score > 0.3) {
        similar.push({ task: otherTask, score });
      }
    }
    
    return similar
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map(item => item.task);
  }
  
  /**
   * 提取关键词
   */
  private extractKeywords(text: string): string[] {
    const stopWords = new Set([
      'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
      'of', 'with', 'by', 'from', 'up', 'about', 'into', 'through', 'during',
      '的', '了', '和', '是', '在', '有', '个', '我', '你', '他', '她'
    ]);
    
    return text
      .toLowerCase()
      .split(/\W+/)
      .filter(word => word.length > 2 && !stopWords.has(word));
  }
}